import { useField } from 'formik';
import { useState } from 'react';
import { NoOptionsMessage } from '@/components/select';
import { useSelectOptions } from '@/components/formBuilder/formFields/SelectField/useSelectOptions';
import { MultiSelectRemoveIcon } from '@/components/select/MultiSelectRemoveIcon';
import { KSelect } from '../../molecules/KSelect/KSelect';
import { KTag, KText } from '@/ui-library/components';
import { CustomOptions } from './CustomOptions';
import { Box, Tooltip } from '@chakra-ui/react';

interface SelectFieldProps {
  item: {
    name: string;
    options?: any[];
    isMulti?: boolean;
    valueKey?: string;
    labelKey?: string;
    fetchConfig?: any;
    debounceTime?: number;
    [key: string]: any;
    isClearable: boolean;
    isSearchable?: boolean;
  };
  field?: any;
  formik?: any;
}

// *************  use prop "customStyles={}" to override the styles, we already have default styles in kselect !!!!!

export const KSelectField = ({ item, formik = {} }: SelectFieldProps) => {
  const {
    name,
    options: staticOptions = [],
    isMulti,
    valueKey = 'value',
    labelKey = 'label',
    fetchConfig = {},
    debounceTime = 500,
    customFormatLabel,
    customOnChange = () => {},
    disabled,
    validationType,
    customOnInputChange,
    customNoOptionsMessage,
    isClearable = false,
    isSearchable = true,
    ...restItemProps
  } = item;

  const [_field, meta, helpers] = useField(name);
  const { setValue, setTouched, setError } = helpers;
  const [inputText, setInputText] = useState('');

  const { optionsData, isLoading, debouncedHandleInputChange } =
    useSelectOptions(fetchConfig, debounceTime);

  const allOptions = [...(staticOptions || []), ...(optionsData || [])];
  const showError = (formik.submitCount > 0 || meta.touched) && meta.error;

  // Utility to flatten grouped options for value matching
  const getFlattenedOptions = (options: any[]) => {
    if (!Array.isArray(options)) return [];
    return options.reduce((acc, group) => {
      if (group.options) {
        // This is a grouped option
        return [...acc, ...group.options];
      }
      // This is a regular option
      return [...acc, group];
    }, []);
  };

  // Get flattened options for value matching (supports both regular and grouped options)
  const flattenedOptions = getFlattenedOptions(allOptions);

  const handleChange = (selectedOption) => {
    let newValue = null;

    if (isMulti && selectedOption) {
      newValue = selectedOption.map((item) => item[valueKey]);
    } else if (selectedOption) {
      newValue = selectedOption[valueKey];
    }

    setValue(newValue);
    customOnChange(selectedOption, formik);
    setTouched(true);
    setError(null);
  };

  const handleInputChange = (text, metaData) => {
    setInputText(text);

    if (customOnInputChange) {
      customOnInputChange(text, metaData);
    }

    const shouldUpdateEndpoint =
      restItemProps.updateEndpointOnChange && text?.length > 1;
    if (shouldUpdateEndpoint) {
      debouncedHandleInputChange(text, metaData);
    }
  };

  // react-select passes `{ context }` telling menu rows apart from the closed
  // trigger. Forwarded so a formatter can relabel only the trigger — without
  // it, `SelectField`'s formatters silently fall through to the raw label.
  const getFormattedOptionLabel = (option, labelMeta) => {
    if (customFormatLabel) {
      return customFormatLabel(option, labelKey, item, labelMeta?.context);
    }
    return option[labelKey];
  };

  const getNoOptionsMessage = (props) => {
    if (customNoOptionsMessage) {
      return customNoOptionsMessage(props);
    }
    return NoOptionsMessage(props);
  };

  const getCurrentValue = () => {
    if (!meta.value) {
      const defaultOption = flattenedOptions.find(
        (item) => item[valueKey] === restItemProps.defaultValue
      );
      return defaultOption || null;
    }
    if (isMulti) {
      // Compared as strings, and against a set rather than the value itself:
      // a string value made `includes` a substring test, so a default of "L5"
      // also picked "L", and an id held as a number never matched one held as
      // a string.
      const picked = new Set(
        (Array.isArray(meta.value) ? meta.value : [meta.value]).map(String)
      );
      return flattenedOptions?.filter((item) =>
        picked.has(String(item[valueKey]))
      );
    }
    if (validationType === 'object') {
      return meta?.value;
    }
    return (
      flattenedOptions?.find((item) => item[valueKey] === meta.value) || null
    );
  };

  const getPlaceholder = () => {
    if (restItemProps.placeholder) {
      return restItemProps.placeholder;
    }

    const labelText = restItemProps.label?.toLowerCase() || '';
    return `Select ${labelText}`;
  };

  return (
    <>
      {restItemProps?.topRightHint && (
        <KTag
          position="absolute"
          right="0"
          top="-3px"
          bg="secondary-50"
          borderRadius="4px"
          size="sm-medium"
          value={restItemProps?.topRightHint}
        ></KTag>
      )}
      <Tooltip
        label={restItemProps?.tooltipLabel}
        hasArrow={true}
        placement={restItemProps?.tooltipPlacement || 'bottom'}
        isDisabled={!disabled || !restItemProps?.tooltipLabel}
      >
        <Box w="100%">
          <KSelect
            isMulti={isMulti}
            isLoading={isLoading || restItemProps?.isLoading}
            options={allOptions}
            inputId={name}
            isClearable={isClearable}
            isSearchable={isSearchable}
            className="kula_custom_select__container"
            classNamePrefix="kula_custom_select"
            formatOptionLabel={getFormattedOptionLabel}
            hasError={showError}
            hasIcon={restItemProps?.showAvatar}
            customComponents={{
              IndicatorSeparator: () => null,
              Option: CustomOptions,
              NoOptionsMessage: getNoOptionsMessage,
              MultiValueRemove: MultiSelectRemoveIcon,
              ...restItemProps.components,
            }}
            customStyles={restItemProps?.customStyles}
            placeholder={getPlaceholder()}
            value={getCurrentValue()}
            getOptionLabel={(option) => option[labelKey]}
            getOptionValue={(option) => option[valueKey]}
            onChange={handleChange}
            onInputChange={handleInputChange}
            inputValue={inputText}
            isOptionDisabled={(option) => Boolean(option.isDisabled)}
            isDisabled={disabled}
            menuPlacement={restItemProps.menuPlacement}
            menuPortalTarget={restItemProps.menuPortalTarget}
            extraProps={{
              valueKey,
              labelKey,
              ...item,
            }}
          />
        </Box>
      </Tooltip>
      {restItemProps.disabled && restItemProps.disabledMessage && (
        <KText mt="5px" color="ats-negative">
          {restItemProps.disabledMessage}
        </KText>
      )}
    </>
  );
};
